import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { CajaRecordType } from './types';
import Table from './components/Table/Table';
import FilterBar from './components/FilterBar/FilterBar';
import { getCajaSessionRecordsByLocalIdAndDateRange } from '../../../../../../crud/management_api/caja_session_records';
import RenderCreateCajaRecord from './components/CreateCajaRecord/CreateCajaRecord';
import LoadingSpinner from '../../../../../../components/common/LoadingSpinner';
import { getAllPendings } from '../../../../../../crud/management_api/local_sessions';
import { Offcanvas, Form } from 'react-bootstrap';
import MultiSelectFilter from '../../../../../../components/common/MultiSelectFilter/MultiSelectFilter';
import { FaFilter, FaPlus } from 'react-icons/fa';
import { X } from 'react-bootstrap-icons';
import DateFilter from '../../../Receipts/components/Filters/DateFilter';
import { exportFilteredArqueos } from './utils/arqueosExcelExport';
import { LocalSession } from '../../../../../../types/LocalSession';

interface FilterOption {
  value: string | number;
  label: string;
}


interface ArqueosProps {
  lastSession?: LocalSession | null;
}

const Arqueos: React.FC<ArqueosProps> = ({ lastSession }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  const [cajaRecords, setCajaRecords] = useState<CajaRecordType[]>([]);
  const [filteredCajaRecords, setFilteredCajaRecords] = useState<CajaRecordType[]>([]);
  
  // Mobile filter state
  const [showFilters, setShowFilters] = useState(false);
  const addDays = (date: Date, days: number) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
  };
  const today = new Date();
  const [startDate, setStartDate] = useState<string>(addDays(today, -6));
  const [endDate, setEndDate] = useState<string>(addDays(today, 1));
  const [estadoFiltro, setEstadoFiltro] = useState<FilterOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [pendingDetails, setPendingDetails] = useState<{ pedidos: number; receipts: number; cajas: number; cash: number }>({ pedidos: 0, receipts: 0, cajas: 0, cash: 0 });

  useEffect(() => {
    getAllPendings(lastSession?.local_session_id ?? 0)
      .then(({ data }) => {
        // Solo marcar como pendiente si hay pedidos, recibos, cajas o cash pendientes (NO solo arqueo)
        const pedidosCount = Array.isArray(data.pending_pedidos) ? data.pending_pedidos.length : 0;
        const receiptsCount = Array.isArray(data.pending_receipts) ? data.pending_receipts.length : 0;
        const cashCount = Array.isArray(data.pending_cash) ? data.pending_cash.length : 0;
        const hasAnyPending = pedidosCount > 0 || receiptsCount > 0 || cashCount > 0;
        setIsPending(hasAnyPending);
        setPendingDetails({ pedidos: pedidosCount, receipts: receiptsCount, cajas: 0, cash: cashCount });
      })
      .catch(error => {
        console.error('Error fetching pending arqueos:', error);
        setIsPending(false);
        setPendingDetails({ pedidos: 0, receipts: 0, cajas: 0, cash: 0 });
      });
  }, [lastSession?.local_session_id]);

  // LocalId y diff (constantes en este caso)
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const localId = user.local_id || 0;

  // Handlers para cambiar fechas
  const handleStartDateChange = (date: string) => {
    setStartDate(date);
  };
  const handleEndDateChange = (date: string) => {
    setEndDate(date);
  };

  // Efecto: Obtener los registros cada vez que cambien fechas, estadoFiltro o soloSesionActual
  const refreshRecords = async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    try {
      const response = await getCajaSessionRecordsByLocalIdAndDateRange(
        localId,
        startDate,
        endDate,
      );
      let filteredRecords: CajaRecordType[] = (response as any).data;
      
      if (estadoFiltro.length > 0) {
        filteredRecords = filteredRecords.filter(record => {
          const estado = record.canceled
            ? 'Cancelado'
            : record.difference === 0
            ? 'Cuadradas'
            : 'No Cuadradas';
          return estadoFiltro.some(filter => filter.value === estado);
        });
      }
      // Ordenar del más reciente al más antiguo por timestamp (o local_timestamp si no hay timestamp)
      filteredRecords.sort((a, b) => {
        const dateA = new Date(a.timestamp || a.local_timestamp).getTime();
        const dateB = new Date(b.timestamp || b.local_timestamp).getTime();
        return dateB - dateA;
      });
      setCajaRecords(filteredRecords);
      setFilteredCajaRecords(filteredRecords);
    } catch (error) {
      console.info('Error al obtener los registros o registro está vacío:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    refreshRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, estadoFiltro]);

  const handleCreateArqueo = () => {
    setShowCreateModal(true);
  };

  // Validation function for mobile create button (same as FilterBar)
  const validateAndCreateArqueoMobile = () => {
    if (isPending) {
      const parts: string[] = [];
      if (pendingDetails?.pedidos) parts.push(`${pendingDetails.pedidos} pedidos`);
      if (pendingDetails?.receipts) parts.push(`${pendingDetails.receipts} recibos`);
      if (pendingDetails?.cajas) parts.push(`${pendingDetails.cajas} cajas`);
      if (pendingDetails?.cash) parts.push(`${pendingDetails.cash} movimientos de caja`);
      const detail = parts.length ? `: ${parts.join(', ')}` : '';
      
      alert(`Hay pendientes${detail}. Deben ser atendidos antes de crear un nuevo arqueo.`);
      return;
    }
    handleCreateArqueo();
  };

  // Helper functions for mobile date filters
  const handleStartDateMobileChange = (newDate: string) => {
    handleStartDateChange(newDate);
  };
  
  const handleEndDateMobileChange = (newDate: string) => {
    handleEndDateChange(newDate);
  };

  // Handle Excel export
  const handleExportToExcel = () => {
    const success = exportFilteredArqueos(filteredCajaRecords, startDate, endDate);
    
    if (success) {
      toast.success('Arqueos exportados exitosamente');
    } else {
      toast.error('No hay arqueos para exportar');
    }
  };

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 200px)' }}>

      {/* Mobile action buttons */}
      <div className="flex items-center gap-2 mb-3 lg:hidden">
        <button className="mp-btn-primary py-2 px-3" onClick={validateAndCreateArqueoMobile}>
          <FaPlus className="text-xs" />
        </button>
        <button className="mp-toolbar-btn" onClick={() => setShowFilters(true)} aria-label="Filtros">
          <FaFilter />
        </button>
      </div>

      {/* Desktop filter bar */}
      <div className="hidden lg:block mt-3 mb-3">
        <FilterBar
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
          estadoFiltro={estadoFiltro}
          onEstadoFiltroChange={setEstadoFiltro}
          onCreateArqueo={handleCreateArqueo}
          isPending={isPending}
          pendingDetails={pendingDetails}
          onExportToExcel={handleExportToExcel}
          exportDisabled={loading}
          totalArqueos={filteredCajaRecords.length}
        />
      </div>

      {/* Offcanvas para móvil (estilo compartido con Historial/Recibos) */}
      <Offcanvas
        show={showFilters}
        onHide={() => setShowFilters(false)}
        placement="end"
        className="mp-offcanvas arqueos-offcanvas"
        backdrop={true}
        scroll={false}
      >
        <Offcanvas.Header className="offcanvas-header">
          <div className="header-content">
            <Offcanvas.Title className="offcanvas-title">Opciones de Filtrado</Offcanvas.Title>
            <div className="section-indicator">Arqueos</div>
          </div>
          <button
            className="close-button"
            onClick={() => setShowFilters(false)}
            aria-label="Cerrar filtros"
          >
            <X size={24} />
          </button>
        </Offcanvas.Header>

        <Offcanvas.Body className="offcanvas-body">
          <div className="filters-container">
            <div className="filter-section">
              <h6 className="filter-section-title">Filtros Generales</h6>

              <div className="filter-group">
                <Form.Label className="filter-label">Rango de fechas</Form.Label>
                <div className="receipt-date-filter-container">
                  <DateFilter
                    startDate={startDate}
                    setStartDate={handleStartDateMobileChange}
                    endDate={endDate}
                    setEndDate={handleEndDateMobileChange}
                  />
                </div>
              </div>

              <div className="filter-group">
                <div className="multiselect-container">
                  <MultiSelectFilter
                    options={[
                      { value: 'Cuadradas', label: 'Cuadradas' },
                      { value: 'No Cuadradas', label: 'No Cuadradas' },
                      { value: 'Cancelado', label: 'Cancelado' }
                    ]}
                    onChange={setEstadoFiltro}
                    placeholder="Filtrar por Estado"
                  />
                </div>
              </div>
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Table container */}
      <div className="flex-1 flex flex-col min-h-0 mt-3">
        {loading ? (
          <LoadingSpinner text="Cargando registros..." transparent={true} />
        ) : (
          <Table
            data={filteredCajaRecords}
            onRecordCreated={refreshRecords}
            lastSessionId={lastSession?.local_session_id ?? null}
            setCurrentData={setFilteredCajaRecords}
          />
        )}
      </div>

      {showCreateModal && (
        <RenderCreateCajaRecord 
          show={showCreateModal} 
          setShow={setShowCreateModal} 
          onRecordCreated={refreshRecords} 
        />
      )}
    </div>
  );
};

export default Arqueos;
